import { apiFetch } from "../utils/apiFetch";
import React, { useCallback, useEffect, useRef, useState } from "react";
import NewClassPopup from "./NewClassPopup";
import ModuleActions from "./ModuleActions";
import ResourceAccessEditor from "./ResourceAccessEditor";
import { useNavigate, useOutletContext } from "react-router";
import { useCourse } from "../context/CourseContext";
import { FiVideo, FiFileText, FiCalendar, FiLock, FiLoader, FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { contentKind, contentDuration } from "../utils/contentLibrary.mjs";
import ContentThumbnail from "./ContentThumbnail";
import ClassPlayback from "./ClassPlayback";
import { useAuth } from "../context/AuthContext";
import ClassDeletePopup from "./ClassDeletePopup";
import { useQueryClient } from "@tanstack/react-query";
import { useGroupSearch } from "../context/GroupSearchContext";
import { useAccountQuery, useGroupClasses } from "../utils/queries";
import { communityJoinPath, lessonAccess, lessonLocked, managesCommunity } from "../utils/communityEntitlements.mjs";

const Classes = () => {
  const [selectedId, setSelectedId] = useState(null);
  const [activeTab, setActiveTab] = useState("videos");
  const [resourceOpen, setResourceOpen] = useState(false);
  const [accessEditor, setAccessEditor] = useState(null);
  const [videoSort, setVideoSort] = useState("latest");
  const { createContentOpen: newClassPopup, setCreateContentOpen: setNewClassPopup } = useOutletContext();
  const [now, setNow] = useState(() => new Date());
  const [rescheduleIndex, setRescheduleIndex] = useState(null);
  const [rescheduleAt, setRescheduleAt] = useState("");
  const [deletePopup, setDeletePopup] = useState(false);
  const [query, setQuery] = useGroupSearch();
  const [localError, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [progressErrors, setProgressErrors] = useState({});
  const progressGeneration = useRef(0);
  const navigate = useNavigate();
  const { course } = useCourse();
  useEffect(() => { setResourceOpen(false); }, [course?._id]);
  const { userId } = useAuth();
  const canTrackProgress = Boolean(userId) && String(course?.creatorId) !== String(userId)
    && Boolean(course?.members?.some(member => String(member.userId?._id || member.userId) === String(userId) && member.role === "member"));
  const trackingEnabled = useRef(canTrackProgress);
  trackingEnabled.current = canTrackProgress;
  const client = useQueryClient();
  const classQuery = useGroupClasses(course?._id);
  const progressQuery = useAccountQuery(["progress", course?._id], `/groups/${course?._id}/class-progress`, {
    enabled: Boolean(course?._id && canTrackProgress),
  });
  const { setClasses } = classQuery;
  const classes = classQuery.data?.classes || null;
  const progress = canTrackProgress ? Object.fromEntries((progressQuery.data?.progress || []).map(item => [String(item.lesson), item])) : {};
  const error = localError || classQuery.error?.message || (canTrackProgress && progressQuery.error?.message) || "";
  const retryQueries = () => { setError(""); classQuery.refetch(); if (canTrackProgress) progressQuery.refetch(); };

  const isOwnerOrAdmin = managesCommunity(course, userId);

  useEffect(() => {
    const updateCurrentTime = () => {
      setNow(new Date());
    };
    updateCurrentTime();
    const interval = setInterval(updateCurrentTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    progressGeneration.current++;
    setNewClassPopup(false); setSelectedId(null);
    setAccessEditor(null);
    setProgressErrors({}); setError(""); setQuery("");
    return () => { progressGeneration.current++; };
  }, [course?._id, userId, canTrackProgress]);

  const saveLessonProgress = useCallback(async (classId, payload) => {
    if (!trackingEnabled.current) return false;
    const generation = progressGeneration.current;
    try {
      const response = await apiFetch(`${process.env.API_URL}/api/classes/${classId}/progress`, {
        method: "POST", keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Could not save progress");
      const queryKey = ["account", userId, "progress", course?._id];
      await client.cancelQueries({ queryKey, exact: true });
      client.setQueryData(queryKey, previous => {
        const rows = previous?.progress || [];
        if ((rows.find(item => String(item.lesson) === classId)?.__v ?? -1) > (data.progress.__v ?? 0)) return previous;
        return { progress: [...rows.filter(item => String(item.lesson) !== classId), data.progress] };
      });
      client.invalidateQueries({ queryKey: ["account", userId, "members", course?._id] });
      if (generation === progressGeneration.current) {
        setProgressErrors(previous => ({ ...previous, [classId]: "" }));
      }
      return true;
    } catch (error) {
      if (generation === progressGeneration.current) setProgressErrors(previous => ({ ...previous, [classId]: error.message }));
      return false;
    }
  }, [client, userId, course?._id]);

  const handleReschedule = async (classId) => {
    if (!rescheduleAt) {
      setError("Please pick a reschedule time.");
      return;
    }
    setBusyId(classId);
    setError("");
    try {
      const res = await apiFetch(
        `${process.env.API_URL}/api/classes/${classId}/reschedule`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",

          },
          body: JSON.stringify({
            scheduledAt: new Date(`${rescheduleAt}+05:30`).toISOString(),
          }),
        },
      );
      const data = await res.json();

      if (!res.ok) {
        throw new Error(`${res.status}: ${data.message}`);
      }

      setClasses((previous) =>
        previous.map((cls) =>
          String(cls._id) === String(classId) ? data.cls : cls,
        ),
      );
      setRescheduleIndex(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const joinLiveClass = async (classId) => {
    setBusyId(classId);
    setError("");
    try {
      const response = await apiFetch(
        `${process.env.API_URL}/api/classes/${classId}/join`,
        {
          method: "POST",
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Could not join class");
      }

      navigate(`/courses/${course.slug}/classes/${classId}/live`, {
        state: {
          token: data.token,
          serverUrl: data.serverUrl,
          isTeacher: data.isTeacher,
          classRecordingStatus: data.classRecordingStatus,
          classTitle: classes?.find(cls => cls._id === classId)?.title,
        },
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const visibleClasses = classes?.filter(cls => {
    const matchesQuery = `${cls.title} ${cls.description || ""} ${(cls.attachments || []).map(att => att.name).join(" ")}`.toLowerCase().includes(query.trim().toLowerCase());
    return matchesQuery && contentKind(cls) === activeTab;
  });
  const ordered = [...(visibleClasses || [])].sort((a, b) => {
    if (activeTab === "videos" && videoSort === "title") return a.title.localeCompare(b.title);
    const delta = (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0);
    return activeTab === "videos" && videoSort === "oldest" ? -delta : delta;
  });
  const selected = ordered.find(cls => cls._id === selectedId) || ordered[0];
  const selectedIndex = ordered.findIndex(cls => cls._id === selected?._id);
  useEffect(() => {
    if (selected && selected._id !== selectedId) {
      setSelectedId(selected._id);
    }
  }, [selected?._id, selectedId]);
  const selectClass = cls => {
    setSelectedId(cls._id);
    setResourceOpen(true);
    setRescheduleIndex(null);
  };

  return (
    <main className="mx-auto w-full px-4 pt-2 pb-6 sm:px-6">
      {error && <div role="alert" className="mb-5 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}{!classes && <button className="ml-3 underline" onClick={retryQueries}>Try again</button>}</div>}
      {!classes && !error ? <div role="status" aria-label="Loading items" className="flex min-h-64 items-center justify-center"><FiLoader className="h-6 w-6 animate-spin text-orange-700" /></div>
        : classes && <div className="min-w-0">
          {!resourceOpen && <>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div role="tablist" aria-label="Content type" className="content-type-tabs">
                {["videos", "photos", "documents"].map(tab => <button key={tab} role="tab" aria-selected={activeTab === tab} onClick={() => { setActiveTab(tab); }} className="content-type-tab">{tab[0].toUpperCase() + tab.slice(1)}</button>)}
              </div>
            </div>
            <div className={activeTab === "videos" ? "content-video-layout" : ""}>
            {activeTab === "videos" && <aside className="content-video-filters"><label className="block text-sm font-semibold">Sort videos<select aria-label="Sort videos" value={videoSort} onChange={event => setVideoSort(event.target.value)} className="mt-2 block w-full rounded-md border border-[var(--app-border)] bg-[var(--app-muted)] p-2"><option value="latest">Latest first</option><option value="oldest">Oldest first</option><option value="title">Title A-Z</option></select></label></aside>}
            <ul aria-label="Content items" className="content-library-grid grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
              {ordered.map(cls => {
                const locked = lessonLocked(cls, course, userId);
                const photo = cls.attachments?.find(att => att.mime?.startsWith("image/"));
                return <li key={cls._id} className="content-library-card relative min-w-0 rounded-lg p-3 shadow-sm">
                  <button onClick={() => selectClass(cls)} aria-label={`Preview ${cls.title}`} className="block w-full text-left">
                    <span className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md bg-[var(--app-muted)]">
                      {!locked && photo ? <img src={`${process.env.API_URL}/api/classes/${cls._id}/documents/${photo._id}?preview=1`} alt="" loading="lazy" className="h-full w-full object-cover" /> : !locked && cls.thumbnail ? <img src={cls.thumbnail} alt="" loading="lazy" className="h-full w-full object-cover" /> : activeTab === "videos" ? <ContentThumbnail key={cls._id} lesson={cls} /> : <FiFileText className="h-8 w-8 text-gray-500" />}
                      {activeTab === "photos" && lessonAccess(cls) === "premium" && <span className="absolute right-2 top-2 rounded-full bg-black/70 p-2 text-white"><FiLock aria-label="Premium" /></span>}
                    </span>
                  </button>
                  <div className="mt-2 flex items-start justify-between gap-1">
                    <button onClick={() => selectClass(cls)} aria-label={cls.title} className="min-w-0 flex-1 py-1 text-left font-semibold"><span className="line-clamp-2 break-words">{cls.title}</span></button>
                    {isOwnerOrAdmin && <ModuleActions editLabel="Edit access" kind="Item" title={cls.title} onEdit={() => setAccessEditor({ lesson: cls })} onDelete={() => setDeletePopup(cls)} />}
                  </div>
                  {activeTab !== "photos" && <><span className="mt-1 inline-block rounded bg-[var(--app-muted)] px-2 py-1 text-xs">{lessonAccess(cls) === "premium" ? "Premium" : "Free"}</span><p className="mt-2 line-clamp-2 text-sm text-gray-500">{cls.description}</p>{activeTab === "videos" && <p className="mt-2 text-xs text-gray-500">{contentDuration(cls)}</p>}</>}
                </li>;
              })}
            </ul>
            </div>
            {!ordered.length && <div className="content-empty-state text-center text-gray-500">{query ? "No matching items." : `No ${activeTab} yet`}</div>}
          </>}
          {resourceOpen && <section aria-label="Selected item" className="w-full min-w-0">
            <button type="button" onClick={() => setResourceOpen(false)} className="mb-3 inline-flex min-h-10 items-center gap-2 rounded-md px-2 text-sm font-semibold"><FiArrowLeft aria-hidden="true" />All {activeTab}</button>
            {!selected && <p className="py-12 text-center text-gray-500">No matching items.</p>}
            {(selected ? [selected] : []).map(cls => {
              const locked = lessonLocked(cls, course, userId);
              const statusStyle = cls.status === "live" ? "bg-red-50 text-red-700" : cls.status === "missed" ? "bg-amber-50 text-amber-800" : "bg-blue-50 text-blue-700";
              return <article key={cls._id} className="min-w-0 overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm">
                {locked ? <div className="flex min-h-[65dvh] flex-col items-center justify-center gap-5 bg-[var(--app-muted)] p-6 text-[var(--app-text)]"><FiLock aria-hidden="true" className="h-10 w-10" /><button onClick={() => navigate(communityJoinPath(course.slug, true))} className="min-h-10 rounded-md bg-orange-700 px-4 font-semibold text-white">Upgrade to view</button></div> : <ClassPlayback key={`${userId}:${cls._id}:${cls.video?.key || cls.recording?.finalVideo?.key || ""}`} lesson={cls} apiUrl={process.env.API_URL}
                  accountId={userId} savedProgress={progress[cls._id]} trackProgress={canTrackProgress} completed={Boolean(progress[cls._id]?.completedAt)} onProgress={payload => saveLessonProgress(cls._id, payload)} />}
                <div className={activeTab === "photos" && !locked ? "grid items-start gap-5 p-4 md:grid-cols-[minmax(0,3fr)_minmax(0,1fr)]" : "p-4"}>
                  <div className={activeTab === "photos" ? "md:col-start-2 md:row-start-1" : ""}><h2 className="font-semibold">{cls.title}</h2>
                  {cls.description && <p className="mt-2 whitespace-pre-wrap break-words text-sm font-medium leading-5 text-gray-800">{cls.description}</p>}
                  </div>
                  {cls.status && <span className={`mt-3 inline-block rounded px-2 py-1 text-xs font-medium capitalize ${statusStyle}`}>{cls.status}</span>}
                  {cls.scheduledAt && <p className="mt-3 flex items-start gap-2 text-xs leading-5 text-gray-600"><FiCalendar aria-hidden="true" className="mt-0.5 shrink-0" /><time dateTime={cls.scheduledAt}>{new Date(cls.scheduledAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} IST</time></p>}
                  {cls.recording?.status === "merging" && !cls.video?.url && !cls.recording?.finalVideo?.url && <p role="status" className="mt-4 flex items-center gap-2 text-sm text-gray-600"><FiLoader className="animate-spin" />Processing recording...</p>}
                  {!locked && cls.attachments?.length > 0 && <div className={activeTab === "photos" ? "md:col-start-1 md:row-start-1" : "mt-2"}>
                    <ul className="divide-y divide-gray-100">{cls.attachments.map((att, index) => <li key={att._id || index}>
                        {["image/jpeg", "image/png", "image/webp", "image/gif"].includes(att.mime) && <img
                          src={`${process.env.API_URL}/api/classes/${cls._id}/documents/${att._id}?preview=1`}
                          alt={att.name || cls.title} draggable={false} onContextMenu={event => event.preventDefault()} loading="lazy" className="w-full max-h-[75dvh] object-contain" />}
                        {att.mime === "application/pdf" && <iframe title={att.name || "Document preview"} src={`${process.env.API_URL}/api/classes/${cls._id}/documents/${att._id}?preview=1#toolbar=0&navpanes=0`} className="h-[75dvh] w-full border-0" />}
                        {!["image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf"].includes(att.mime) && <p className="p-6 text-sm text-gray-600">Preview unavailable for this format. Ask the owner for a PDF version.</p>}
                    </li>)}</ul>
                  </div>}
                  {!locked && cls.status && cls.status !== "ended" && <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
                    <button disabled={Boolean(busyId) || now.getTime() < new Date(cls.scheduledAt).getTime() - 5 * 60 * 1000 || cls.status === "missed"} onClick={() => joinLiveClass(cls._id)}
                      className="flex h-9 items-center gap-2 rounded-md bg-orange-700 px-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"><FiVideo aria-hidden="true" />{busyId === cls._id ? "Please wait..." : "Join class"}</button>
                    {isOwnerOrAdmin && ["missed", "scheduled"].includes(cls.status) && <button onClick={() => { setRescheduleIndex(rescheduleIndex === cls._id ? null : cls._id); setRescheduleAt(""); }} className="flex h-9 items-center gap-2 rounded-md border border-gray-300 px-3 text-sm font-medium"><FiCalendar aria-hidden="true" />Reschedule</button>}
                  </div>}
                  {rescheduleIndex === cls._id && <form onSubmit={event => { event.preventDefault(); handleReschedule(cls._id); }} className="mt-4 flex flex-wrap items-end gap-3">
                    <label className="min-w-0 flex-1 text-xs text-gray-600">New date and time (IST)<input required type="datetime-local" value={rescheduleAt} onChange={event => setRescheduleAt(event.target.value)} className="mt-1 block h-10 w-full min-w-0 rounded-md border border-gray-300 px-2 text-sm text-gray-900" /></label>
                    <button disabled={Boolean(busyId)} className="h-10 rounded-md bg-gray-900 px-3 text-sm font-medium text-white disabled:opacity-50">Save</button>
                  </form>}
                  {progressErrors[cls._id] && <p role="alert" className="mt-2 text-xs text-red-700">{progressErrors[cls._id]}</p>}
                </div>
              </article>;
            })}
            {selected && <div className="mt-4 flex items-center justify-between gap-3">
              <button disabled={selectedIndex <= 0} onClick={() => selectClass(ordered[selectedIndex - 1])} className="inline-flex min-h-10 items-center gap-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:text-gray-400"><FiArrowLeft aria-hidden="true" />Previous</button>
              <span className="text-xs text-gray-600">{selectedIndex + 1} / {ordered.length}</span>
              <button disabled={selectedIndex >= ordered.length - 1} onClick={() => selectClass(ordered[selectedIndex + 1])} className="inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-orange-800 disabled:cursor-not-allowed disabled:text-gray-400">Next item<FiArrowRight aria-hidden="true" /></button>
            </div>}
          </section>}
          </div>}
      {deletePopup && <ClassDeletePopup setDeletePopup={setDeletePopup} classTitle={deletePopup.title} classId={deletePopup._id} setClasses={setClasses} />}
      {accessEditor && <ResourceAccessEditor course={course} target={accessEditor} onClose={() => setAccessEditor(null)} onSaved={async () => { const result = await classQuery.refetch(); if (result.error) throw result.error; }} />}
      {newClassPopup && <NewClassPopup setNewClassPopup={setNewClassPopup} workspace={course} setClasses={setClasses} />}
    </main>
  );
};

export default Classes;
